"use client";
import { BriefcaseBusiness, ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { blogs } from "@/lib/blogs";

const BlogSec = ({ data }) => {
    // Create a more efficient blog lookup - only filter once
    const getBlogsByCategory = (category) => {
        return blogs.filter(blog => blog.category === category);
    };

    const targetCategory = data.slug;
    const displayBlogs = getBlogsByCategory(targetCategory).slice(0, 3);

    return (
        <section
            className='py-16 px-4 sm:px-6 lg:py-24 lg:px-12 bg-gray-50/50'
            aria-labelledby="blog-section-title"
        >
            <div className='max-w-7xl mx-auto flex flex-col items-center gap-12 lg:gap-16'>

                {/* Heading Section */}
                <header className="text-center flex flex-col items-center gap-6 w-full">
                    <span className="inline-flex items-center gap-2 border-2 border-[#4F7AC2] text-[#4F7AC2] px-5 py-2 rounded-full text-xs font-bold shadow-sm w-fit uppercase tracking-widest bg-white">
                        <BriefcaseBusiness className="w-4 h-4" aria-hidden="true" />
                        Key Insights
                    </span>
                    <h2
                        id="blog-section-title"
                        className='max-w-4xl text-3xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight'
                    >
                        Insights for <span className="text-[#4F7AC2]">Smarter Decisions</span>
                    </h2>
                </header>

                {/* Blog Grid */}
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                    {displayBlogs.map((blog, index) => (
                        <article
                            key={blog.slug || index}
                            className={`group bg-white rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col border border-gray-100 overflow-hidden focus-within:ring-2 focus-within:ring-[#4F7AC2] focus-within:ring-offset-2 ${index === 2 ? 'hidden lg:flex' : 'flex'
                                }`}
                        >
                            {/* Image Container with Link */}
                            <Link
                                href={`/blog/${blog.slug}`}
                                className="relative aspect-[16/10] w-full overflow-hidden block"
                                aria-label={`Read article: ${blog.title}`}
                            >
                                <Image
                                    src={blog.image}
                                    alt={`Featured image for ${blog.title}`}
                                    fill
                                    className="object-cover group-hover:scale-105 transition-transform duration-700"
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                />
                                {/* Optional: Category Overlay on Image */}
                                <div className="absolute top-4 left-4">
                                    <span className="bg-white/90 backdrop-blur-sm text-[#4F7AC2] text-[10px] px-3 py-1 rounded-lg font-bold uppercase tracking-wider shadow-sm">
                                        {blog.category}
                                    </span>
                                </div>
                            </Link>

                            <div className="flex flex-col flex-1 p-8">
                                <div className="flex items-center justify-between mb-5">
                                    {/*<time
                                        dateTime={blog.date}
                                        className="text-xs text-gray-500 font-bold uppercase tracking-wide"
                                    >
                                        {blog.date}
                                    </time>*/}
                                    
                                    {/* Reading Time Badge */}
                                    <span className="bg-gray-100 text-gray-600 text-[10px] px-3 py-1 rounded-full font-extrabold uppercase tracking-tighter">
                                        {blog.readingTime}
                                    </span>
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-4">
                                    {blog.title}
                                </h3>

                                <p className="text-base text-gray-600 leading-relaxed line-clamp-3 mb-8">
                                    {blog.description}
                                </p>

                                <div className="mt-auto">
                                    
                                    <Link
                                        href={`/blog/${blog.slug}`}
                                        className="inline-flex w-full h-11 items-center justify-center gap-2 bg-brand hover:bg-[#3d5f99] text-white rounded-md font-semibold transition-all focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                                    >
                                        Read Full Article
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <footer className="pt-4">
                    <Link href="/blog">
                        <span className="inline-flex items-center gap-2 text-[#4F7AC2] font-bold hover:gap-3 transition-all text-lg cursor-pointer">
                            View all insights
                            <ArrowRight className="w-5 h-5" />
                        </span>
                    </Link>
                </footer>
            </div>
        </section>
    );
}

export default BlogSec;
